import React, { useState } from "react";

import SingleMetaField from "src/app/Task/components/TaskMetaFields/SingleMetaField";
import { CustomSwitch } from "src/app/components/CustomSwitch/CustomSwitch";
import { tableDataType } from "src/app/components/Table/Table";
import { MetaFieldType } from "src/app/methods/getMetaFieldTypeOptions";
import {
  MetaFieldIcon,
  renderTooltipIcon,
} from "src/app/modals/ManageFieldModal/components/GlobalFields/utils";
import { RemoveModal } from "src/app/modals/RemoveModal/RemoveModal";
import { ReactComponent as GridIcon } from "src/images/grid.svg";
import { ReactComponent as TrashCanIcon } from "src/images/trash-can-gray.svg";
import { GlobalProjectMetaField } from "src/redux/settings/settingsReducer";
import useSortableItem from "src/app/components/VerticalDragAndDropList/useSortableItem";
import {
  FieldOption,
  HandleRemoveFieldsProps,
  HandleUpdateTemplateValueParams,
  TemplateProjectField,
} from "../types";
import { ALLOWED_FIELD_TYPES } from "../utils";

interface DraggableAttributeListItemProps {
  option: FieldOption;
  globalField: GlobalProjectMetaField;
  templateField: TemplateProjectField;
  handleUpdateTemplateValue: (params: HandleUpdateTemplateValueParams) => void;
  handleRemoveFields: (params: HandleRemoveFieldsProps) => void;
}

export default function DraggableAttributeListItem({
  option,
  globalField,
  templateField,
  handleUpdateTemplateValue,
  handleRemoveFields,
}: DraggableAttributeListItemProps) {
  const [isDefaultValueChecked, setIsDefaultValueChecked] = useState(
    templateField.defaultValue !== null,
  );
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);

  const { attributes, listeners, setNodeRef, style } = useSortableItem(
    option.value,
  );

  const fieldType = "project";
  const taskType = "null";

  const globalFieldWithDefaultValue =
    globalField.type === MetaFieldType.Date &&
    typeof templateField.defaultValue === "string"
      ? { ...globalField, value: { date: templateField.defaultValue } }
      : { ...globalField, value: templateField.defaultValue };

  return (
    <>
      <li
        ref={setNodeRef}
        style={style}
        className="template-manager__list-item grid--attributes"
        data-qa-field-name={option.label}
      >
        <div className="template-manager__text">
          <div {...attributes} {...listeners} className="icon-drag">
            <GridIcon />
          </div>
          <div className="template-manager__col">
            <div className="template-manager__name">
              <MetaFieldIcon
                icon={renderTooltipIcon(
                  globalField.type,
                  globalField.valueSource,
                )}
              />
              <span className="template-manager__name-text">
                {globalField.name}
              </span>
            </div>
            <div className="template-manager__metaField-value">
              {isDefaultValueChecked && (
                <SingleMetaField
                  metaData={globalFieldWithDefaultValue}
                  dataType={tableDataType.TemplateFields}
                  handleUpdateMetaValue={(newValue) => {
                    handleUpdateTemplateValue({
                      uuid: templateField.uuid,
                      valueFieldName: "defaultValue",
                      updatedValue: newValue,
                      fieldType,
                      taskType,
                    });
                  }}
                />
              )}
            </div>
          </div>
        </div>
        <CustomSwitch
          checked={templateField.showInCampaignCreator}
          disabled={!ALLOWED_FIELD_TYPES.includes(globalField.type)}
          onChange={(e) =>
            handleUpdateTemplateValue({
              uuid: templateField.uuid,
              valueFieldName: "showInCampaignCreator",
              updatedValue: e.target.checked,
              fieldType,
              taskType,
            })
          }
        />
        <CustomSwitch
          checked={templateField.valueRequired}
          onChange={(e) =>
            handleUpdateTemplateValue({
              uuid: templateField.uuid,
              valueFieldName: "valueRequired",
              updatedValue: e.target.checked,
              fieldType,
              taskType,
            })
          }
        />
        <div className="template-manager__list-actions">
          <CustomSwitch
            checked={isDefaultValueChecked}
            disabled={
              !ALLOWED_FIELD_TYPES.includes(globalField.type) ||
              globalField.valueSource !== null
            }
            onChange={(e) => {
              const { checked } = e.target;
              setIsDefaultValueChecked(checked);
              if (!checked) {
                handleUpdateTemplateValue({
                  uuid: templateField.uuid,
                  valueFieldName: "defaultValue",
                  updatedValue: null,
                  fieldType,
                  taskType,
                });
              }
            }}
          />
          <button
            aria-label="Remove field"
            type="button"
            className="button-style-reset"
            onClick={() => setIsRemoveModalOpen(true)}
          >
            <TrashCanIcon />
          </button>
        </div>
      </li>
      {isRemoveModalOpen && (
        <RemoveModal
          objectNames={[globalField.name]}
          onClose={() => setIsRemoveModalOpen(false)}
          removeFunction={() =>
            handleRemoveFields({
              fieldsToRemove: [templateField.uuid],
              fieldType,
              taskType: "null",
            })
          }
        />
      )}
    </>
  );
}
